import { API, BACKEND_URL } from '@/config/api';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { useConfirm } from '@/components/ConfirmDialog';
import { toast } from 'sonner';
import AdminNavigation from '@/components/AdminNavigation';
import PageHeader from './BulkSampleEntry/components/PageHeader';
import SampleFormHeader from './BulkSampleEntry/components/SampleFormHeader';
import DraftStatusIndicator from './BulkSampleEntry/components/DraftStatusIndicator';
import TableSectionHeader from './BulkSampleEntry/components/TableSectionHeader';
import ManualOrganismForm from './BulkSampleEntry/components/ManualOrganismForm';
import AddGroupDialog from './BulkSampleEntry/components/AddGroupDialog';
import AddSpeciesDialog from './BulkSampleEntry/components/AddSpeciesDialog';
import AddZooplanktonForm from './BulkSampleEntry/components/AddZooplanktonForm';
import AddSampleTypeDialog from './BulkSampleEntry/components/AddSampleTypeDialog';
import AddZooplanktonOrganismDialog from './BulkSampleEntry/components/AddZooplanktonOrganismDialog';
import SummaryModal from './BulkSampleEntry/components/SummaryModal';import FullscreenAlgaeModal from './BulkSampleEntry/components/FullscreenAlgaeModal';
import FullscreenZooplanktonModal from './BulkSampleEntry/components/FullscreenZooplanktonModal';
import ContextMenu from './BulkSampleEntry/components/ContextMenu';
import LocationModal from './BulkSampleEntry/components/LocationModal';
import PastePreviewDialog from './BulkSampleEntry/components/PastePreviewDialog';
import SaveTemplateDialog from './BulkSampleEntry/components/SaveTemplateDialog';
import BiovolumDialog from './BulkSampleEntry/components/BiovolumDialog';
import AlgaeTableActions from './BulkSampleEntry/components/AlgaeTableActions';
import ProgressIndicator from './BulkSampleEntry/components/ProgressIndicator';
import ColorLegend from './BulkSampleEntry/components/ColorLegend';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { 
  Save, Loader2, Plus, Trash2, X, ChevronDown, ChevronRight, MapPin, Calendar as CalendarIcon, 
  Download, Upload, Eye, Send, AlertTriangle, CheckCircle, Info, FileSpreadsheet, Clipboard,
  ArrowLeft, Settings, RefreshCw, Microscope, Edit2, Copy, MoreHorizontal, Search, Sparkles, Zap, BarChart3
} from 'lucide-react';
import { 
  DEFAULT_LOCATIONS, 
  DEFAULT_ZOOPLANKTON_ORGANISMS, 
  DEFAULT_ORGANISMS,
  ORGANISM_GROUPS,
  GROUP_ICONS,
  GROUP_COLORS,
  SPECIES_BY_GROUP,
  DEFAULT_SAMPLE_TYPES,
  DEFAULT_ORGANISMS_BY_SAMPLE_TYPE
} from './BulkSampleEntry/utils/constants';
import { getColorForValue as getColorForValueUtil } from './BulkSampleEntry/utils/gridCalculations';
import { downloadAlgaeTemplate } from './BulkSampleEntry/utils/dataFormatters';
import { formatDisplayValue, formatZooplanktonDisplayValue, getRawValue } from './BulkSampleEntry/utils/gridOperations';
import { 
  addNewSampleTypeHelper, 
  addNewZooplanktonOrganismHelper,
  insertZooplanktonRowAfterHelper 
} from './BulkSampleEntry/utils/zooplanktonHelpers';
import {
  addNewGroupHelper,
  addNewSpeciesHelper,
  getFilteredGroupsHelper,
  getFilteredSpeciesHelper
} from './BulkSampleEntry/utils/organismHelpers';


import {
  addManualRowHelper,
  removeCustomRowHelper,
  removeRowHelper,
  removeZooplanktonRowHelper,
  handleZooplanktonRowRightClickHelper
} from './BulkSampleEntry/utils/rowManagement';
import { autoPopulateAllFieldsHelper } from './BulkSampleEntry/utils/testDataGenerator';
import { calculateSumsHelper } from './BulkSampleEntry/utils/calculations';
import { 
  generateAISummaryHelper, 
  saveWithoutSendingHelper, 
  finalSaveHelper 
} from './BulkSampleEntry/utils/submissionHandlers';
import {
  createNewLocationHelper,
  selectLocationForColumnHelper,
  removeDynamicColumnHelper,
  removeStandardColumnHelper
} from './BulkSampleEntry/utils/columnManagement';
import {
  updateGridValueHelper,
  updateZooplanktonValueHelper,
  handleRightClickHelper,
  handleRowRightClickHelper,
  setColorOverrideHelper,
  removeColorOverrideHelper,
  updateDepthHelper,
  computeAutoColor
} from './BulkSampleEntry/utils/gridEventHandlers';
import {
  handlePasteClickHelper,
  applyPastedDataHelper
} from './BulkSampleEntry/utils/pasteHandlers';
import { useDraftManagement } from './BulkSampleEntry/hooks/useDraftManagement';
import { useValidation } from './BulkSampleEntry/hooks/useValidation';
import { useOrganismsFetching } from './BulkSampleEntry/hooks/useOrganismsFetching';
import { useTemplateManagement } from './BulkSampleEntry/hooks/useTemplateManagement';
import { useInitialDataLoading } from './BulkSampleEntry/hooks/useInitialDataLoading';
import { usePageProtection } from './BulkSampleEntry/hooks/usePageProtection';
import { useAuth } from '@/App';
import { isDemoUser } from '@/lib/utils';

// Use imported constants
const locations = DEFAULT_LOCATIONS;
const zooplanktonOrganisms = DEFAULT_ZOOPLANKTON_ORGANISMS;

export default function BulkSampleEntry() {
  const navigate = useNavigate();
  const { bulkId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isDemo = isDemoUser(user);
  const confirmDialog = useConfirm();
  const isNewRegistration = searchParams.get('new') === '1';
  const draftIdFromUrl = searchParams.get('draft');
  
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [areas, setAreas] = useState([]);
  const [sampleDate, setSampleDate] = useState(new Date());
  const [selectedArea, setSelectedArea] = useState('');
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualGroup, setManualGroup] = useState('');
  const [manualSpecies, setManualSpecies] = useState('');
  const [manualDepth, setManualDepth] = useState('');
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [speciesSearchTerm, setSpeciesSearchTerm] = useState('');
  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);
  const [showAddSpeciesDialog, setShowAddSpeciesDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newSpeciesName, setNewSpeciesName] = useState('');
  const [organismGroups, setOrganismGroups] = useState(ORGANISM_GROUPS);
  const [customOrganisms, setCustomOrganisms] = useState([]);
  const [colorOverrides, setColorOverrides] = useState({});
  const [contextMenu, setContextMenu] = useState(null);
  const [showSummaryStep, setShowSummaryStep] = useState(false);  const [generatedSummary, setGeneratedSummary] = useState('');
  const [editableSummary, setEditableSummary] = useState('');
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [fullscreenAlgae, setFullscreenAlgae] = useState(false);
  const [fullscreenZooplankton, setFullscreenZooplankton] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);
  
  // Temperature input modal
  const [tempModalOpen, setTempModalOpen] = useState(false);
  const [tempModalLoc, setTempModalLoc] = useState('');
  const [tempModalLabel, setTempModalLabel] = useState('');
  const [tempModalValue, setTempModalValue] = useState('');
  
  const openTempModal = (loc, label) => {
    setTempModalLoc(loc);
    setTempModalLabel(label);
    setTempModalValue(temperatureData[loc] || '');
    setTempModalOpen(true);
  };
  
  const saveTempModal = () => {
    setTemperatureData(prev => ({ ...prev, [tempModalLoc]: tempModalValue }));
    setTempModalOpen(false);
  };
  
  // Dynamic schema-based arrays - initialized as empty, populated in useEffect
  const [currentLocations, setCurrentLocations] = useState([]);
  
  // Dynamic columns (for adding new locations inline)
  const [dynamicColumns, setDynamicColumns] = useState([]);
  const [columnSelectMode, setColumnSelectMode] = useState({});
  const [openLocationPopover, setOpenLocationPopover] = useState({});
  
  // New location modal state
  const [showNewLocationModal, setShowNewLocationModal] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationLetter, setNewLocationLetter] = useState('');
  const [newLocationAddress, setNewLocationAddress] = useState('');
  const [newLocationLatitude, setNewLocationLatitude] = useState('');
  const [newLocationLongitude, setNewLocationLongitude] = useState('');
  const [newLocationAreaId, setNewLocationAreaId] = useState('');
  const [pendingColumnId, setPendingColumnId] = useState(null);
  const [allAvailableLocations, setAllAvailableLocations] = useState([]);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  
  // Depth overrides for organisms (key: organismKey, value: depth)
  const [depthOverrides, setDepthOverrides] = useState({});
  
  // Temperature per location (key: locationName, value: temperature string)
  const [temperatureData, setTemperatureData] = useState({});
  
  // Sum threshold dialog state
  const [showSumThresholdDialog, setShowSumThresholdDialog] = useState(false);
  const [sumThresholdGroup, setSumThresholdGroup] = useState('');
  const [sumThresholds, setSumThresholds] = useState({});
  
  // Biovolume distribution dialog state
  const [showBiovolumDialog, setShowBiovolumDialog] = useState(false);
  const [biovolumCellData, setBiovolumCellData] = useState(null); // { organismKey, locationKey, value, organism }
  const [biovolumDistribution, setBiovolumDistribution] = useState({}); // { class_id: percentage }
  const [biovolumData, setBiovolumData] = useState({}); // Stores all biovolum distributions
  
  // Zooplankton organism management - will be loaded dynamically
  const [currentZooplanktonOrganisms, setCurrentZooplanktonOrganisms] = useState([...zooplanktonOrganisms]);
  const [customZooplanktonOrganisms, setCustomZooplanktonOrganisms] = useState([]);
  const [showZooplanktonForm, setShowZooplanktonForm] = useState(false);
  const [zooplanktonSampleType, setZooplanktonSampleType] = useState('');
  const [zooplanktonOrganism, setZooplanktonOrganism] = useState('');
  const [sampleTypeSearchTerm, setSampleTypeSearchTerm] = useState('');
  const [zooplanktonOrganismSearchTerm, setZooplanktonOrganismSearchTerm] = useState('');
  const [showAddSampleTypeDialog, setShowAddSampleTypeDialog] = useState(false);
  const [showAddZooplanktonOrganismDialog, setShowAddZooplanktonOrganismDialog] = useState(false);
  const [newSampleTypeName, setNewSampleTypeName] = useState('');
  const [newZooplanktonOrganismName, setNewZooplanktonOrganismName] = useState('');
  const [sampleTypes, setSampleTypes] = useState(DEFAULT_SAMPLE_TYPES);
  const [organismsBySampleType, setOrganismsBySampleType] = useState(DEFAULT_ORGANISMS_BY_SAMPLE_TYPE);
  
  // Separate grid data for zooplankton
  const [zooplanktonGridData, setZooplanktonGridData] = useState(() => {
    const initial = {};
    zooplanktonOrganisms.forEach(org => {
      const key = `${org.sampleType}_${org.organism}`;
      initial[key] = {};
      locations.forEach(loc => {
        initial[key][loc] = '';
      });
    });
    return initial;
  });
  
  // Paste functionality states
  const [showPasteDialog, setShowPasteDialog] = useState(false);
  const [pastePreviewData, setPastePreviewData] = useState([]);
  const [pasteTargetTable, setPasteTargetTable] = useState(''); // 'algae' or 'zooplankton'
  
  // Ref to track when paste operation is in progress (to prevent draft loading from overwriting)
  const isPastingRef = React.useRef(false);
  
  // Reset all state when "Ny prøve" is clicked (navigating with ?new=1)
  useEffect(() => {
    if (isNewRegistration) {
      setSampleDate(new Date());
      setSelectedArea('');
      setSelectedTemplate('');
      setGridData({});
      setZooplanktonGridData(() => {
        const initial = {};
        zooplanktonOrganisms.forEach(org => {
          const key = `${org.sampleType}_${org.organism}`;
          initial[key] = {};
          locations.forEach(loc => { initial[key][loc] = ''; });
        });
        return initial;
      });
      setColorOverrides({});
      setIsEditMode(false);
      setEditableSummary('');
      setGeneratedSummary('');
      resetDraftId();
      // Clean the URL param so refresh loads draft
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [isNewRegistration]);
  
  // Fetch schema configurations from backend on mount
  useEffect(() => {
    const fetchSchemas = async () => {
      try {
        const response = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
        setAvailableTemplates(response.data);
      } catch (error) {
        console.error('Error fetching schemas:', error);
        // Fallback to empty array if fetch fails
        setAvailableTemplates([]);
      }
    };
    
    fetchSchemas();
  }, []); // Run once on mount
  
  // Existing groups and species for dropdowns with icons
  const existingGroups = ORGANISM_GROUPS;
  
  // Dynamic organisms state - will be loaded from backend
  const [organisms, setOrganisms] = useState(DEFAULT_ORGANISMS);
  
  // Use imported constants as defaults, but will be updated from API
  const groupIcons = GROUP_ICONS;
  const groupColors = GROUP_COLORS;
  const [speciesByGroup, setSpeciesByGroup] = useState(SPECIES_BY_GROUP);
  
  const [gridData, setGridData] = useState({});
  
  // Use draft management hook
  const {
    draftSaved,
    lastDraftTime,
    currentDraftId,
    loadDraft,
    saveDraft,
    clearDraft,
    resetDraftId,
    setDraftSaved,
    setLastDraftTime,
    setCurrentDraftId,
    draftLoadedRef
  } = useDraftManagement({
    gridData,
    zooplanktonGridData,
    sampleDate,
    selectedArea,
    customOrganisms,
    colorOverrides,
    depthOverrides,
    temperatureData,
    currentZooplanktonOrganisms,
    organisms,
    setOrganisms,
    setCurrentZooplanktonOrganisms,
    setGridData,
    setZooplanktonGridData,
    setSampleDate,
    setSelectedArea,
    setCustomOrganisms,
    setColorOverrides,
    setDepthOverrides,
    setTemperatureData,
    zooplanktonOrganisms,
    locations,
    bulkId,
    selectedTemplate,
    setSelectedTemplate,
    setCurrentLocations,
    currentLocations
  });
  
  // Determine if current template has zooplankton
  const hasZooplankton = (() => {
    const tmpl = availableTemplates.find(t => t.id === selectedTemplate);
    if (tmpl?.config?.has_zooplankton === false) return false;
    return true; // default: true
  })();

  // Determine if current template has algae
  const hasAlgae = (() => {
    const tmpl = availableTemplates.find(t => t.id === selectedTemplate);
    if (tmpl?.config?.has_algae === false) return false;
    return true; // default: true
  })();

  // Use validation hook
  const {
    validateAllFieldsFilled,
    getCompletionPercentage
  } = useValidation({
    organisms,
    customOrganisms,
    currentLocations,
    locations,
    gridData,
    currentZooplanktonOrganisms,
    zooplanktonGridData,
    hasZooplankton,
    hasAlgae
  });
  
  // Use organisms fetching hook
  const { fetchOrganisms } = useOrganismsFetching({
    locations,
    setOrganisms,
    setCurrentZooplanktonOrganisms,
    setSampleTypes,
    setOrganismsBySampleType,
    setZooplanktonGridData,
    setGridData,
    availableTemplates,
    selectedTemplate
  });
  
  // Define calculateSums function before hooks that need it
  const calculateSums = (data) => {
    return calculateSumsHelper(data, currentLocations, locations);
  };
  
  // Use schema management hook
  useTemplateManagement({
    locations,
    organisms,
    speciesByGroup,
    availableTemplates,
    setAvailableTemplates,
    selectedTemplate,
    customOrganisms,
    currentZooplanktonOrganisms,
    setCurrentLocations,
    setGridData,
    setZooplanktonGridData,
    calculateSums,
    isEditMode,
    draftLoadedRef
  });
  
  // Use initial data loading hook
  const { fetchAreas } = useInitialDataLoading({
    bulkId,
    isNewRegistration,
    draftIdFromUrl,
    navigate,
    setIsEditMode,
    setAreas,
    setAllAvailableLocations,
    setSampleDate,
    setSelectedArea,
    setGridData,
    setZooplanktonGridData,
    setColorOverrides,
    setTemperatureData,
    setEditableSummary,
    setGeneratedSummary,
    setCurrentLocations,
    setSelectedTemplate,
    setCurrentZooplanktonOrganisms,
    setOrganisms,
    loadDraft,
    isPastingRef,
    confirmDialog
  });
  
  // Call fetchOrganisms once on mount, separately from other initial loading
  // BUT: Skip if draft/edit data has already loaded organisms
  const hasFetchedOrganismsRef = useRef(false);
  useEffect(() => {
    if (hasFetchedOrganismsRef.current) return;
    
    const timerRef = setTimeout(() => {
      hasFetchedOrganismsRef.current = true;
      
      // If organisms are already populated (from draft/edit), just check thresholds
      if (organisms.length > 0) {
        const hasThresholds = organisms.some(o => o.threshold_red != null);
        if (!hasThresholds) {
          fetchOrganisms();
        }
        return;
      }
      
      // Safety net: organisms still empty after 800ms — something went wrong
      // with draft/edit loading. Fetch organisms to prevent blank tables.
      console.warn('⚠️ Organisms empty after 800ms, fetching from backend as safety net');
      fetchOrganisms();
    }, 800);
    
    return () => clearTimeout(timerRef);
  }, [fetchOrganisms, organisms, isEditMode]);

  // Re-calculate colorOverrides when organisms are loaded with thresholds
  const prevThresholdsRef = useRef(false);
  useEffect(() => {
    const hasThresholds = organisms.some(o => o.threshold_red != null);
    if (!hasThresholds) return;
    if (prevThresholdsRef.current) return; // Only run once when thresholds first arrive
    prevThresholdsRef.current = true;
    
    // Recalculate colors for all existing grid data based on thresholds
    const newColors = {};
    Object.entries(gridData).forEach(([organismKey, locData]) => {
      const parts = organismKey.split('_');
      const group = parts[0] || '';
      const species = parts.slice(1).join('_') || '';
      Object.entries(locData).forEach(([location, value]) => {
        const autoColor = computeAutoColor(value, group, species, organisms);
        if (autoColor) {
          newColors[`${organismKey}_${location}`] = autoColor;
        }
      });
    });
    if (Object.keys(newColors).length > 0) {
      setColorOverrides(prev => ({ ...prev, ...newColors }));
    }
  }, [organisms]);

  // Fetch all organism groups and species from database
  useEffect(() => {
    const fetchOrganismGroups = async () => {
      try {
        const response = await axios.get(`${API}/organisms`, { withCredentials: true });
        const allOrganisms = response.data.filter(org => org.type === 'algae');
        
        // Build groups and species map from database
        const groupsFromDB = new Set();
        const speciesMap = {};
        
        allOrganisms.forEach(org => {
          const group = org.group || 'Ukjent';
          groupsFromDB.add(group);
          
          if (!speciesMap[group]) {
            speciesMap[group] = [];
          }
          if (org.species && !speciesMap[group].includes(org.species)) {
            speciesMap[group].push(org.species);
          }
        });
        
        // Update state with data from database
        const newGroups = Array.from(groupsFromDB);
        if (newGroups.length > 0) {
          setOrganismGroups(prev => {
            // Merge with existing groups (keep order of existing, add new at end)
            const merged = [...prev];
            newGroups.forEach(g => {
              if (!merged.includes(g)) {
                merged.push(g);
              }
            });
            return merged;
          });
          
          setSpeciesByGroup(prev => ({
            ...prev,
            ...speciesMap
          }));
          
        }
      } catch (error) {
        console.error('Error fetching organism groups:', error);
      }
    };
    
    fetchOrganismGroups();
  }, []);
  
  // Use page protection hook (beforeunload warning and auto-save)
  const { saveNow } = usePageProtection({
    gridData,
    zooplanktonGridData,
    sampleDate,
    selectedArea,
    customOrganisms,
    colorOverrides,
    depthOverrides,
    temperatureData,
    saveDraft,
    isPastingRef
  });
  
  // Force save on blur events — set <5000 default for empty cells
  const handleInputBlur = async (e, organismKey, location) => {
    const rawValue = getRawValue(e.target.value);
    const currentValue = rawValue || '';
    
    // If cell is empty after interaction, default to <5000
    if (currentValue === '' && !organismKey.toUpperCase().includes('SUM')) {
      updateGridValue(organismKey, location, '<5000');
    } else if (rawValue !== e.target.value) {
      updateGridValue(organismKey, location, rawValue);
    }
    // Schedule save after React has flushed state updates (150ms allows re-render + ref sync)
    // Using saveNow (ref-based) ensures we always save the latest data
    setTimeout(() => saveNow(), 150);
  };
  
  
  // TEST FUNCTION - Auto-populate all fields for demo
  const autoPopulateAllFields = () => {
    autoPopulateAllFieldsHelper(
      organisms,
      locations,
      zooplanktonOrganisms,
      calculateSums,
      setGridData,
      setZooplanktonGridData,
      setColorOverrides,
      toast
    );
  };
  
  const updateGridValue = (organismKey, location, value) => {
    updateGridValueHelper(organismKey, location, value, calculateSums, setGridData, organisms, setColorOverrides);
  };
  
  const updateZooplanktonValue = (organismKey, location, value) => {
    updateZooplanktonValueHelper(organismKey, location, value, setZooplanktonGridData);
  };
  
  
  // Use utility function for color calculation - include organisms for threshold lookup
  const getColorForValue = (value, group, species, organismKey, location) => {
    return getColorForValueUtil(value, group, species, organismKey, location, colorOverrides, organisms);
  };
  
  const handleRightClick = (e, organismKey, location) => {
    handleRightClickHelper(e, organismKey, location, setContextMenu);
  };
  
  const handleRowRightClick = (e, organismKey, organism) => {
    handleRowRightClickHelper(e, organismKey, organism, setContextMenu);
  };
  
  const setColorOverride = (color) => {
    setColorOverrideHelper(contextMenu, color, setColorOverrides, setContextMenu);
  };
  
  const removeColorOverride = () => {
    removeColorOverrideHelper(contextMenu, setColorOverrides, setContextMenu);
  };
  
  // Paste functionality
  const handlePasteClick = async (tableType) => {
    await handlePasteClickHelper(tableType, setPastePreviewData, setPasteTargetTable, setShowPasteDialog);
  };
  
  const applyPastedData = () => {
    // Set flag to prevent auto-save/draft-loading from overwriting pasted data
    isPastingRef.current = true;
    
    applyPastedDataHelper({
      pasteTargetTable,
      pastePreviewData,
      organisms,
      currentLocations,
      locations,
      gridData,
      zooplanktonGridData,
      currentZooplanktonOrganisms,
      calculateSums,
      setGridData,
      setZooplanktonGridData,
      setShowPasteDialog,
      setColorOverrides
    });
    
    // Re-enable draft operations after 1 second AND save immediately
    setTimeout(async () => {
      isPastingRef.current = false;
      
      // Immediately save draft after paste to ensure data is persisted
      await saveDraft();
    }, 1000); // Reduced from 3000ms to 1000ms for faster save
  };
  
  const handleSubmitAll = async () => {
    if (!selectedArea) {
      toast.error('Velg område først');
      return;
    }
    
    // Validate all fields are filled
    const missingFields = validateAllFieldsFilled();
    if (missingFields.length > 0) {
      toast.error(`Følgende felter mangler data: ${missingFields.slice(0, 3).join(', ')}${missingFields.length > 3 ? ` og ${missingFields.length - 3} flere` : ''}`);
      return;
    }
    
    // Go directly to AI report generation
    await generateAISummary();
  };
  
  const generateAISummary = async () => {
    await generateAISummaryHelper({
      sampleDate,
      selectedArea,
      areas,
      gridData,
      zooplanktonGridData,
      organisms,
      customOrganisms,
      setGeneratedSummary,
      setEditableSummary,
      setShowSummaryStep,
      setGeneratingSummary,
      isDemo
    });
  };
  
  const saveWithoutSending = async () => {
    await saveWithoutSendingHelper({
      isEditMode,
      bulkId,
      sampleDate,
      selectedArea,
      areas,
      gridData,
      zooplanktonGridData,
      colorOverrides,
      temperatureData,
      editableSummary,
      validateAllFieldsFilled,
      clearDraft,
      navigate,
      setLoading,
      isDemo,
      currentDraftId,
      userName: user?.name,
      currentLocations
    });
  };
  
  const finalSave = async () => {
    await finalSaveHelper({
      isEditMode,
      bulkId,
      sampleDate,
      selectedArea,
      areas,
      gridData,
      zooplanktonGridData,
      colorOverrides,
      temperatureData,
      editableSummary,
      organisms,
      customOrganisms,
      clearDraft,
      navigate,
      setLoading,
      isDemo,
      userName: user?.name,
      currentLocations
    });
  };
  
  const downloadTemplate = () => {
    downloadAlgaeTemplate(organisms, customOrganisms, locations, sampleDate);
  };
  
  // Open dialog to save as NEW schema
  const handleSaveAsNewTemplate = () => {
    setIsUpdatingTemplate(false);
    setShowSaveTemplateDialog(true);
  };
  
  // Open dialog to UPDATE current schema
  const handleUpdateCurrentTemplate = () => {
    setIsUpdatingTemplate(true);
    setShowSaveTemplateDialog(true);
  };
  
  // Save or update schema configuration
  const handleSaveTemplate = async ({ name, description, has_algae, has_zooplankton }) => {
    // Get active locations (use currentLocations if set, otherwise fall back to default locations)
    const activeLocations = currentLocations.length > 0 ? currentLocations : locations;
    
    
    // Build schema config from current state (outside try block for catch to access)
    const schemaConfig = {
        has_algae: has_algae !== false,
        has_zooplankton: has_zooplankton !== false,
        locations: activeLocations
          .filter(loc => loc && typeof loc === 'string') // Filter out undefined/null values
          .map((loc, idx) => {
            const parts = loc.split(' - ');
            const letter = parts[0];
            const locName = parts[1] || '';
            return {
              id: `loc-${letter.toLowerCase()}`,
              letter: letter,
              name: locName,
              full_name: loc
            };
          }),
        organisms: organisms
          .filter(o => o.isSpecies && !o.isSum)
          .map(o => ({
            id: `${o.group}_${o.species}`,
            group: o.group,
            species: o.species,
            depth: o.depth || '',
            isSpecies: true
          }))
      };
    
    
    try {
      if (isUpdatingTemplate) {
        // UPDATE existing schema configuration
        const currentConfig = availableTemplates.find(t => t.id === selectedTemplate);
        
        // Check if schema config exists in backend first
        if (!currentConfig || !selectedTemplate || selectedTemplate === 'default-template') {
          // Schema config doesn't exist in backend or is default - create as NEW instead
          
          // If user is trying to "update" default schema, suggest a new name
          let finalName = name;
          if (!selectedTemplate || selectedTemplate === 'default-template' && name === 'Standard P02 Rogaland (A-L)') {
            finalName = `Min Tilpassede Skjema ${new Date().toLocaleDateString('no-NO')}`;
          }
          
          const createPayload = {
            name: finalName,
            description,
            is_default: false,
            config: schemaConfig
          };
          
          const response = await axios.post(`${API}/bulk-templates`, createPayload, { withCredentials: true });
          
          // Refresh schema configurations
          const schemasResponse = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
          setAvailableTemplates(schemasResponse.data);
          setSelectedTemplate(response.data.id);
          toast.success('Nytt skjema opprettet basert på nåværende oppsett!');
        } else {
          // Schema config exists - update it
          const updateData = {
            name,
            description,
            config: schemaConfig,
            updated_at: new Date().toISOString()
          };
          
          await axios.put(`${API}/bulk-templates/${selectedTemplate}`, updateData, { withCredentials: true });
          
          // Refresh schema configurations
          const response = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
          setAvailableTemplates(response.data);
          toast.success('Skjema oppdatert!');
        }
      } else {
        // CREATE new schema - save to backend
        const createPayload = {
          name,
          description,
          is_default: false,
          config: schemaConfig
        };
        
        const response = await axios.post(`${API}/bulk-templates`, createPayload, { withCredentials: true });
        
        // Refresh schemas
        const schemasResponse = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
        setAvailableTemplates(schemasResponse.data);
        setSelectedTemplate(response.data.id);
        toast.success('Nytt skjema lagret!');
      }
      
      setShowSaveTemplateDialog(false);
    } catch (error) {
      console.error('Error saving schema:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Schema config being sent:', schemaConfig);
      toast.error(`Feil ved lagring av skjema: ${error.response?.data?.detail || error.message}`);
    }
  };
  
  // Filter groups based on search
  const getFilteredGroups = () => {
    return getFilteredGroupsHelper(organismGroups, groupSearchTerm);
  };

  // Filter species based on search
  const getFilteredSpecies = () => {
    const species = speciesByGroup[manualGroup] || [];
    return getFilteredSpeciesHelper(species, speciesSearchTerm);
  };

  // Add new group
  const addNewGroup = () => {
    if (!newGroupName.trim()) {
      toast.error('Vennligst skriv inn gruppenavn');
      return;
    }
    
    if (organismGroups.includes(newGroupName)) {
      toast.error('Denne gruppen finnes allerede');
      return;
    }
    
    setOrganismGroups(prev => [...prev, newGroupName]);
    setSpeciesByGroup(prev => ({
      ...prev,
      [newGroupName]: []
    }));
    setManualGroup(newGroupName);
    setNewGroupName('');
    setShowAddGroupDialog(false);
    toast.success(`Gruppen "${newGroupName}" ble lagt til`);
  };

  // Add new species
  const addNewSpecies = () => {
    if (!newSpeciesName.trim()) {
      toast.error('Vennligst skriv inn artsnavn');
      return;
    }
    
    if (!manualGroup) {
      toast.error('Velg en gruppe først');
      return;
    }
    
    const existingSpecies = speciesByGroup[manualGroup] || [];
    if (existingSpecies.includes(newSpeciesName)) {
      toast.error('Denne arten finnes allerede i gruppen');
      return;
    }
    
    setSpeciesByGroup(prev => ({
      ...prev,
      [manualGroup]: [...(prev[manualGroup] || []), newSpeciesName]
    }));
    setManualSpecies(newSpeciesName);
    setNewSpeciesName('');
    setShowAddSpeciesDialog(false);
    toast.success(`Arten "${newSpeciesName}" ble lagt til`);
  };

  // Callback to save organism permanently to template
  const handleSaveOrganismToTemplate = async (newOrganism) => {
    
    // Get current template config
    const currentConfig = availableTemplates.find(t => t.id === selectedTemplate);
    if (!currentConfig || !selectedTemplate || selectedTemplate === 'default-template') {
      toast.info('Organismen er lagt til for denne økten. Lagre skjemaet for å beholde permanent.');
      return;
    }
    
    try {
      // Add the new organism to the template's config
      const updatedOrganisms = [
        ...(currentConfig.config?.organisms || []),
        {
          id: `${newOrganism.group}_${newOrganism.species}`,
          group: newOrganism.group,
          species: newOrganism.species,
          depth: newOrganism.depth || '',
          isSpecies: true
        }
      ];
      
      const updateData = {
        ...currentConfig,
        config: {
          ...currentConfig.config,
          organisms: updatedOrganisms
        },
        updated_at: new Date().toISOString()
      };
      
      await axios.put(`${API}/bulk-templates/${selectedTemplate}`, updateData, { withCredentials: true });
      
      // Refresh templates list
      const response = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
      setAvailableTemplates(response.data);
      
      toast.success(`"${newOrganism.species}" lagret permanent i skjemaet!`);
    } catch (error) {
      console.error('Error saving organism to template:', error);
      toast.error('Kunne ikke lagre organismen permanent');
    }
  };
  
  const addManualRow = (savePermanently = false) => {
    addManualRowHelper(
      manualGroup,
      manualSpecies,
      manualDepth,
      currentLocations,
      setCustomOrganisms,
      setOrganisms,
      setGridData,
      setManualGroup,
      setManualSpecies,
      setManualDepth,
      setShowManualForm,
      toast,
      savePermanently,
      savePermanently ? handleSaveOrganismToTemplate : null
    );
  };

  // Zooplankton functions
  const getFilteredSampleTypes = () => {
    if (!sampleTypeSearchTerm) return sampleTypes;
    return sampleTypes.filter(t => 
      t.toLowerCase().includes(sampleTypeSearchTerm.toLowerCase())
    );
  };

  const getFilteredZooplanktonOrganisms = () => {
    
    if (!zooplanktonSampleType || !organismsBySampleType[zooplanktonSampleType]) {
      return [];
    }
    
    const organisms = organismsBySampleType[zooplanktonSampleType];
    
    if (!zooplanktonOrganismSearchTerm) return organisms;
    
    const filtered = organisms.filter(o => 
      o.toLowerCase().includes(zooplanktonOrganismSearchTerm.toLowerCase())
    );
    
    return filtered;
  };

  const addNewSampleType = () => {
    addNewSampleTypeHelper(
      newSampleTypeName,
      sampleTypes,
      setSampleTypes,
      setZooplanktonSampleType,
      setOrganismsBySampleType,
      setNewSampleTypeName,
      setShowAddSampleTypeDialog,
      toast
    );
  };

  const addNewZooplanktonOrganism = () => {
    addNewZooplanktonOrganismHelper(
      newZooplanktonOrganismName,
      zooplanktonSampleType,
      organismsBySampleType,
      setOrganismsBySampleType,
      setZooplanktonOrganism,
      setNewZooplanktonOrganismName,
      setShowAddZooplanktonOrganismDialog,
      toast
    );
  };

  const addZooplanktonRow = () => {
    if (!zooplanktonSampleType.trim() || !zooplanktonOrganism.trim()) {
      toast.error('Fyll ut både prøvetype og organisme');
      return;
    }
    
    const newOrganism = {
      sampleType: zooplanktonSampleType.trim(),
      organism: zooplanktonOrganism.trim(),
      isSpecies: true,
      isCustom: true
    };
    
    setCustomZooplanktonOrganisms(prev => [...prev, newOrganism]);
    setCurrentZooplanktonOrganisms(prev => [...prev, newOrganism]);
    
    const organismKey = `${newOrganism.sampleType}_${newOrganism.organism}`;
    setZooplanktonGridData(prev => ({
      ...prev,
      [organismKey]: currentLocations.reduce((acc, loc) => ({ ...acc, [loc]: '' }), {})
    }));
    
    setZooplanktonSampleType('');
    setZooplanktonOrganism('');
    setShowZooplanktonForm(false);
    
    toast.success('Ny zooplankton-rad lagt til');
  };

  const insertZooplanktonRowAfter = (index) => {
    insertZooplanktonRowAfterHelper(index, setShowZooplanktonForm);
  };

  const removeZooplanktonRow = async (organismKey) => {
    removeZooplanktonRowHelper(
      organismKey,
      currentZooplanktonOrganisms,
      setCurrentZooplanktonOrganisms,
      customZooplanktonOrganisms,
      setCustomZooplanktonOrganisms,
      zooplanktonGridData,
      setZooplanktonGridData,
      toast
    );
    
    // Remove zooplankton from backend as well
    try {
      const [sampleType, organism] = organismKey.split('_');
      await axios.delete(`${API}/organisms/zooplankton/${sampleType}/${organism}`, { withCredentials: true });
      toast.success('Zooplankton fjernet permanent');
    } catch (error) {
      console.error('Error removing zooplankton from backend:', error);
      // Still show success since it was removed from UI
    }
  };

  const handleZooplanktonRowRightClick = (e, organismKey, organism) => {
    handleZooplanktonRowRightClickHelper(e, organismKey, organism, setContextMenu);
  };
  
  const removeCustomRow = (organismKey) => {
    removeCustomRowHelper(
      organismKey,
      customOrganisms,
      setCustomOrganisms,
      gridData,
      setGridData,
      calculateSums,
      toast
    );
    // Also remove from the display organisms array
    setOrganisms(prev => prev.filter(org => `${org.group}_${org.species}` !== organismKey));
  };
  
  const handleSaveTemplateWithDeletion = async (organismKey, organism) => {
    // Remove organism from current template
    const currentConfig = availableTemplates.find(t => t.id === selectedTemplate);
    if (!currentConfig || !selectedTemplate || selectedTemplate === 'default-template') {
      toast.info('Organismen er fjerna for denne økten.');
      return;
    }
    
    try {
      const updatedOrganisms = (currentConfig.config?.organisms || []).filter(
        org => `${org.group}_${org.species}` !== organismKey
      );
      
      const updateData = {
        ...currentConfig,
        config: {
          ...currentConfig.config,
          organisms: updatedOrganisms
        },
        updated_at: new Date().toISOString()
      };
      
      await axios.put(`${API}/bulk-templates/${selectedTemplate}`, updateData, { withCredentials: true });
      
      // Refresh templates
      const response = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
      setAvailableTemplates(response.data);
    } catch (error) {
      console.error('Error updating template after deletion:', error);
      toast.error('Kunne ikkje oppdatere malen');
    }
  };

  const removeRow = async (organismKey, organism) => {
    const currentTemplateName = availableTemplates.find(t => t.id === selectedTemplate)?.name || null;
    await removeRowHelper(
      organismKey,
      organism,
      removeCustomRow,
      setOrganisms,
      setGridData,
      calculateSums,
      setDepthOverrides,
      toast,
      isDemo,
      confirmDialog,
      handleSaveTemplateWithDeletion,
      currentTemplateName
    );
  };
  
  const addDynamicColumn = () => {
    const newColId = `dynamic-col-${Date.now()}`;
    setDynamicColumns(prev => [...prev, { id: newColId, location: null }]);
    setColumnSelectMode(prev => ({ ...prev, [newColId]: true }));
  };
  
  const selectLocationForColumn = (columnId, locationFullName) => {
    selectLocationForColumnHelper({
      columnId,
      locationFullName,
      organisms,
      gridData,
      calculateSums,
      setPendingColumnId,
      setShowNewLocationModal,
      setDynamicColumns,
      setColumnSelectMode,
      setGridData
    });
  };
  
  const createNewLocation = async () => {
    await createNewLocationHelper({
      newLocationLetter,
      newLocationName,
      newLocationAddress,
      newLocationLatitude,
      newLocationLongitude,
      newLocationAreaId,
      allAvailableLocations,
      gridData,
      zooplanktonGridData,
      pendingColumnId,
      setLoading,
      setAllAvailableLocations,
      setCurrentLocations,
      setGridData,
      setZooplanktonGridData,
      selectLocationForColumn,
      setPendingColumnId,
      resetLocationForm,
      setShowNewLocationModal,
      isDemo
    });
  };
  
  const resetLocationForm = () => {
    setNewLocationName('');
    setNewLocationLetter('');
    setNewLocationAddress('');
    setNewLocationLatitude('');
    setNewLocationLongitude('');
    setNewLocationAreaId('');
  };
  
  const removeDynamicColumn = (columnId) => {
    removeDynamicColumnHelper({
      columnId,
      dynamicColumns,
      gridData,
      setGridData,
      setDynamicColumns,
      setOpenLocationPopover,
      setColumnSelectMode
    });
  };
  
  const removeStandardColumn = (locationName) => {
    removeStandardColumnHelper({
      locationName,
      gridData,
      zooplanktonGridData,
      setCurrentLocations,
      setGridData,
      setZooplanktonGridData
    });
  };

  const addColumn = () => {
    setShowNewLocationModal(true);
  };
  
  const updateDepth = (organismKey, newDepth) => {
    updateDepthHelper(organismKey, newDepth, setDepthOverrides);
  };
  
  const insertColumnBefore = (index) => {
    const newColId = `dynamic-col-${Date.now()}`;
    const newColumn = { 
      id: newColId, 
      location: null, 
      insertAt: index // Track where to insert
    };
    
    // Insert at specific position in dynamicColumns
    setDynamicColumns(prev => {
      const updated = [...prev];
      updated.splice(index, 0, newColumn);
      return updated;
    });
    setColumnSelectMode(prev => ({ ...prev, [newColId]: true }));
  };
  
  const insertRowAfter = (index) => {
    // Open the add organism modal
    setShowManualForm(true);
    // Store the index where the new row should be inserted
    // For now, we'll just add to the end via the existing modal
    // A future enhancement could be to specify insertion point
  };
  
  // Merge current locations with dynamic columns at their insert positions
  const getMergedColumns = () => {
    const merged = [];
    let dynamicIndex = 0;
    
    currentLocations.forEach((loc, index) => {
      // Add any dynamic columns that should come before this location
      while (dynamicIndex < dynamicColumns.length && 
             (dynamicColumns[dynamicIndex].insertAt <= index || 
              (dynamicColumns[dynamicIndex].insertAt === undefined && index === 0))) {
        merged.push({ type: 'dynamic', data: dynamicColumns[dynamicIndex] });
        dynamicIndex++;
      }
      
      // Add the regular location
      merged.push({ type: 'location', data: loc, originalIndex: index });
    });
    
    // Add remaining dynamic columns at the end
    while (dynamicIndex < dynamicColumns.length) {
      merged.push({ type: 'dynamic', data: dynamicColumns[dynamicIndex] });
      dynamicIndex++;
    }
    
    return merged;
  };
  
  // Only locations in dynamic columns (not the schema/standard locations)
  const usedDynamicLocations = dynamicColumns.filter(c => c.location).map(c => c.location);
  
  return (
    <>
      <AdminNavigation />
      <div className="min-h-screen bg-slate-50/50 lg:pl-[240px]">
        <div className="max-w-full mx-auto px-3 sm:px-6 py-4 sm:py-6">
          <PageHeader />
          
          {/* Compact header controls */}
          <SampleFormHeader
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            availableTemplates={availableTemplates}
            setAvailableTemplates={setAvailableTemplates}
            sampleDate={sampleDate}
            setSampleDate={setSampleDate}
            selectedArea={selectedArea}
            setSelectedArea={setSelectedArea}
            areas={areas}
            handleSubmitAll={handleSubmitAll}
            loading={loading}
            generatingSummary={generatingSummary}
            getCompletionPercentage={getCompletionPercentage}
            hasUnsavedData={false}
            onSaveDraft={saveDraft}
            draftSaved={draftSaved}
            onEditSections={handleUpdateCurrentTemplate}
          />
          
        <div className="max-w-full mx-auto px-2 sm:px-4 py-2 sm:py-3">
          
          {/* Show tables only when both template and area are selected */}
          {(!selectedArea || !selectedTemplate) && !isEditMode ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-slate-300" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-1">
                {!selectedTemplate ? 'Vel skjema for å starte' : 'Vel område for å starte'}
              </h3>
              <p className="text-sm text-slate-400 max-w-md mx-auto">
                {!selectedTemplate 
                  ? 'Vel skjema (mal) og område i feltet over for å opne registreringstabell'
                  : 'Vel prøvedato og område i skjemaet over for å opne registreringstabell'
                }
              </p>
            </div>
          ) : (
          <>
          {/* Draft status - always visible */}
          <div className="flex justify-end mb-2">
            <DraftStatusIndicator
              lastDraftTime={lastDraftTime}
              draftSaved={draftSaved}
              colorOverrides={colorOverrides}
              onClearDraft={clearDraft}
            />
          </div>
          
          {/* Algae section — hidden when template has no algae */}
          {hasAlgae && (
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <TableSectionHeader
                  title="Algeprøver"
                  onPasteClick={() => handlePasteClick('algae')}
                  onFullscreenClick={() => setFullscreenAlgae(true)}
                />
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-lg">
            <div className="relative overflow-x-auto overflow-y-auto max-h-[400px] sm:max-h-[500px]">
              <table className="w-full border-collapse min-w-max">
                <thead className="sticky top-0 z-50">
                  <tr className="bg-slate-50 border-b-2 border-slate-300">
                    <th className="sticky left-0 z-[60] border-r border-slate-200 px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50" style={{width: '130px'}}>
                      Orden/gruppe
                    </th>
                    <th className="sticky z-[60] border-r border-slate-200 px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50" style={{left: '130px', width: '150px'}}>
                      Evt. art
                    </th>
                    <th className="sticky z-[60] border-r border-slate-200 px-2 py-2.5 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50" style={{left: '280px', width: '60px', boxShadow: '3px 0 6px rgba(0,0,0,0.08)'}}>
                      <div className="text-xs font-semibold text-slate-600">Djup</div>
                      <div className="text-xs text-slate-500">(m)</div>
                    </th>
                    {getMergedColumns().map((col, displayIndex) => {
                      if (col.type === 'location') {
                        const loc = col.data;
                        const index = col.originalIndex;
                        // Parse location string - handles both "A - Toftøy" and "Lokalitet A - Toftøy" formats
                        let letter = String.fromCharCode(65 + index);
                        let name = 'Ny lokalitet';
                        
                        if (loc) {
                          const parts = loc.split(' - ');
                          if (parts.length >= 2) {
                            // Format: "A - Toftøy" or "Lokalitet A - Toftøy"
                            const firstPart = parts[0].trim();
                            // Check if it starts with "Lokalitet"
                            if (firstPart.startsWith('Lokalitet ')) {
                              letter = firstPart.replace('Lokalitet ', '').trim();
                            } else {
                              letter = firstPart;
                            }
                            name = parts.slice(1).join(' - ').trim();
                          } else {
                            // No " - " separator, use as name
                            name = loc;
                          }
                        }
                        
                        return (
                          <th key={loc} className="border-r border-slate-200 px-1.5 py-2 text-center bg-slate-50 min-w-[90px] group/col relative">
                            {/* Delete column - subtle */}
                            <button
                              onClick={() => removeStandardColumn(loc)}
                              className="absolute top-0.5 right-0.5 w-4 h-4 rounded opacity-0 group-hover/col:opacity-100 transition-all flex items-center justify-center text-slate-400 hover:text-red-500 z-30"
                              title="Fjern kolonne"
                            >
                              <X className="w-3 h-3" />
                            </button>

                            {/* Excel-style insert column line on left edge */}
                            {displayIndex === 0 && (
                              <div className="absolute -left-px top-0 bottom-0 w-[3px] group/ins z-40 cursor-col-resize" onClick={() => insertColumnBefore(0)}>
                                <div className="absolute inset-0 opacity-0 hover:opacity-100 bg-teal-500 transition-opacity" />
                                <div className="absolute top-1/2 -translate-y-1/2 -left-1.5 w-4 h-4 bg-teal-500 rounded-full opacity-0 group-hover/ins:opacity-100 transition-all flex items-center justify-center shadow-sm">
                                  <Plus className="w-2.5 h-2.5 text-white" />
                                </div>
                              </div>
                            )}

                            {/* Excel-style insert column line on right edge */}
                            <div className="absolute -right-px top-0 bottom-0 w-[3px] group/ins z-40 cursor-col-resize" onClick={() => insertColumnBefore(index + 1)}>
                              <div className="absolute inset-0 opacity-0 hover:opacity-100 bg-teal-500 transition-opacity" />
                              <div className="absolute top-1/2 -translate-y-1/2 -right-1.5 w-4 h-4 bg-teal-500 rounded-full opacity-0 group-hover/ins:opacity-100 transition-all flex items-center justify-center shadow-sm">
                                <Plus className="w-2.5 h-2.5 text-white" />
                              </div>
                            </div>
                            
                            <div>
                              <div className="flex items-center gap-1 justify-center">
                                <span className="text-xs font-semibold text-slate-700">{letter} - {name}</span>
                                <span className="text-[10px] text-slate-400">(ant/l)</span>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTempModal(loc, `${letter} - ${name}`);
                                }}
                                className={`mt-1 mx-auto flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] transition-colors ${
                                  temperatureData[loc] 
                                    ? 'bg-sky-100 text-sky-700 font-semibold border border-sky-200' 
                                    : 'bg-slate-100 text-slate-400 border border-slate-200 hover:bg-sky-50 hover:text-sky-600 hover:border-sky-200'
                                }`}
                                title="Registrer temperatur"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z"/></svg>
                                {temperatureData[loc] ? `${temperatureData[loc]}°C` : '— °C'}
                              </button>
                            </div>
                          </th>
                        );
                      } else {
                        // Dynamic column
                        const column = col.data;
                        return (
                          <th key={column.id} className="border-r border-slate-300 px-2 py-2 text-center text-sm font-semibold bg-yellow-50 min-w-32 relative group">
                            {columnSelectMode[column.id] ? (
                              <div className="flex items-center space-x-1">
                                <Popover 
                                  open={openLocationPopover[column.id]} 
                                  onOpenChange={(open) => {
                                    setOpenLocationPopover(prev => ({ ...prev, [column.id]: open }));
                                    if (!open) {
                                      setLocationSearchQuery('');
                                    }
                                  }}
                                >
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      role="combobox"
                                      className="h-8 flex-1 justify-between text-xs border-yellow-300 hover:bg-yellow-100"
                                    >
                                      <Search className="w-3 h-3 mr-1" />
                                      Velg lokalitet...
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-80 p-0" align="start">
                                    <Command>
                                      <CommandInput 
                                        placeholder="Søk lokalitet..." 
                                        value={locationSearchQuery}
                                        onValueChange={setLocationSearchQuery}
                                      />
                                      <CommandList>
                                        <CommandEmpty>
                                          <div className="py-6 text-center">
                                            <p className="text-sm text-slate-500 mb-3">Ingen lokaliteter funnet</p>
                                            <Button
                                              onClick={() => {
                                                selectLocationForColumn(column.id, '__NEW__');
                                                setOpenLocationPopover(prev => ({ ...prev, [column.id]: false }));
                                                setLocationSearchQuery('');
                                              }}
                                              size="sm"
                                              className="bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 text-white"
                                            >
                                              <Plus className="w-4 h-4 mr-2" />
                                              Opprett ny lokalitet
                                            </Button>
                                          </div>
                                        </CommandEmpty>
                                        <CommandGroup>
                                          {allAvailableLocations
                                            .filter(loc => {
                                              const fullName = `Lokalitet ${loc.letter} - ${loc.name}`;
                                              return !usedDynamicLocations.includes(fullName);
                                            })
                                            .map((loc) => {
                                              const fullName = `Lokalitet ${loc.letter} - ${loc.name}`;
                                              return (
                                                <CommandItem
                                                  key={loc.id}
                                                  value={fullName}
                                                  onSelect={() => {
                                                    selectLocationForColumn(column.id, fullName);
                                                    setOpenLocationPopover(prev => ({ ...prev, [column.id]: false }));
                                                    setLocationSearchQuery('');
                                                  }}
                                                  className="cursor-pointer"
                                                >
                                                  <div className="flex items-center space-x-2 flex-1">
                                                    <div className="w-6 h-6 bg-teal-100 rounded flex items-center justify-center flex-shrink-0">
                                                      <span className="text-teal-700 font-bold text-xs">{loc.letter}</span>
                                                    </div>
                                                    <span className="text-sm">{fullName}</span>
                                                  </div>
                                                </CommandItem>
                                              );
                                            })}
                                          <CommandItem
                                            value="__NEW__"
                                            onSelect={() => {
                                              selectLocationForColumn(column.id, '__NEW__');
                                              setOpenLocationPopover(prev => ({ ...prev, [column.id]: false }));
                                              setLocationSearchQuery('');
                                            }}
                                            className="cursor-pointer border-t border-slate-200 mt-1 bg-teal-50 hover:bg-teal-100"
                                          >
                                            <div className="flex items-center space-x-2 text-teal-600 font-semibold">
                                              <Plus className="w-4 h-4" />
                                              <span>Ny lokalitet...</span>
                                            </div>
                                          </CommandItem>
                                        </CommandGroup>
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                                {/* Delete button when in select mode */}
                                <Button
                                  onClick={() => removeDynamicColumn(column.id)}
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  title="Slett kolonne"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-xs font-bold">{column.location?.split(' - ')[0] || 'Ny'}</div>
                                  <div className="text-xs text-slate-500">(ant/l)</div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {column.location && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openTempModal(column.location, column.location?.split(' - ')[0] || 'Lokasjon');
                                      }}
                                      className={`flex items-center gap-0.5 px-1.5 py-1 rounded text-[10px] transition-colors ${
                                        temperatureData[column.location]
                                          ? 'bg-sky-100 text-sky-700 hover:bg-sky-200 font-semibold'
                                          : 'bg-slate-50 text-slate-300 hover:bg-sky-50 hover:text-sky-500'
                                      }`}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z"/></svg>
                                      {temperatureData[column.location] ? `${temperatureData[column.location]}°` : '°C'}
                                    </button>
                                  )}
                                </div>
                                {/* Delete button when location is selected */}
                                <Button
                                  onClick={() => removeDynamicColumn(column.id)}
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Slett kolonne"
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </th>
                        );
                      }
                    })}
                    
                    {/* Permanent + button column for adding new locations */}
                    <th className="border-r border-slate-300 px-2 py-2 text-center bg-slate-50 w-16">
                      <button
                        onClick={() => setShowNewLocationModal(true)}
                        className="w-10 h-10 bg-teal-600 hover:bg-teal-700 text-white rounded-full flex items-center justify-center shadow-md hover:scale-110 transition-all mx-auto"
                        title="Legg til lokalitet"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {organisms.map((organism, index) => {
                    const organismKey = `${organism.group}_${organism.species}`;
                    const isSum = organism.isSum || organism.isTotal;
                    const isTotal = organism.isTotal;
                    const isCustom = organism.isCustom;
                    
                    return (
                      <tr 
                        key={organismKey}
                        className={`group relative ${isSum ? 'bg-slate-100 font-medium' : 'hover:bg-slate-50'} ${isTotal ? 'bg-blue-100 font-medium' : ''} ${isCustom ? 'bg-yellow-50' : ''}`}
                        onContextMenu={(e) => handleRowRightClick(e, organismKey, organism)}
                      >
                        <td className={`sticky left-0 border-r border-b border-slate-300 px-3 py-2 text-sm ${isSum ? 'bg-slate-100' : 'bg-white'} ${isTotal ? 'bg-blue-100' : ''} ${isCustom ? 'bg-yellow-50' : ''} relative`} style={{width: '130px', zIndex: 40}}>
                          <div className="flex items-center justify-between">
                            <span>{organism.group}</span>
                            {/* Pencil button for sum rows to edit thresholds */}
                            {isSum && !isTotal && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSumThresholdGroup(organism.group);
                                  setShowSumThresholdDialog(true);
                                }}
                                className="ml-2 w-6 h-6 bg-blue-500 hover:bg-blue-600 text-white rounded-full opacity-0 group-hover:opacity-100 hover:!opacity-100 transition-all duration-200 flex items-center justify-center shadow-lg hover:scale-110 cursor-pointer flex-shrink-0"
                                title="Rediger grenseverdier for sum"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                            )}
                            {/* Plus button as inline element */}
                            {!isSum && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  insertRowAfter(index);
                                }}
                                className="ml-2 w-6 h-6 bg-teal-600 hover:bg-teal-700 text-white rounded-full opacity-0 group-hover:opacity-100 hover:!opacity-100 transition-all duration-200 flex items-center justify-center shadow-lg hover:scale-110 cursor-pointer flex-shrink-0"
                                title="Legg til rad etter"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className={`sticky border-r border-b border-slate-300 px-3 py-2 text-sm ${isSum ? 'bg-slate-100' : 'bg-white'} ${isTotal ? 'bg-blue-100' : ''} ${isCustom ? 'bg-yellow-50' : ''}`} style={{zIndex: 40, left: '130px', width: '150px'}}>
                          <div className="flex items-center justify-between">
                            <span className={isSum ? 'font-medium' : 'italic'}>{organism.species}</span>
                            <div className="flex items-center space-x-1">
                              {isCustom && <span className="text-xs bg-yellow-200 text-yellow-800 px-1 rounded">ny</span>}
                              {isCustom && (
                                <Button
                                  onClick={() => removeCustomRow(organismKey)}
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  title="Slett rad"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className={`sticky border-r border-b border-slate-300 px-2 py-2 text-center text-sm w-20 ${isSum ? 'bg-slate-100' : 'bg-white'} ${isTotal ? 'bg-blue-100' : ''} ${isCustom ? 'bg-yellow-50' : ''}`} style={{left: '280px', zIndex: 39, boxShadow: '3px 0 6px rgba(0,0,0,0.08)'}}>
                          {organism.isSum || organism.isTotal ? (
                            <div className="text-sm font-medium text-slate-400">-</div>
                          ) : (
                            <Input 
                              type="number"
                              value={depthOverrides[organismKey] !== undefined ? depthOverrides[organismKey] : organism.depth}
                              onChange={(e) => updateDepth(organismKey, e.target.value)}
                              className="h-8 text-sm text-center border-0 focus:ring-2 focus:ring-blue-500 rounded-md font-semibold bg-blue-50 hover:bg-blue-100 cursor-pointer"
                              placeholder="Djup"
                              min="0"
                              step="0.5"
                            />
                          )}
                        </td>
                        {getMergedColumns().map((col) => {
                          if (col.type === 'location') {
                            const location = col.data;
                            return (
                              <td key={location} className="border-r border-b border-slate-300 p-1">
                                {isSum ? (
                                  <div className={`h-8 flex items-center justify-center text-sm font-bold ${isTotal ? 'text-blue-900 bg-blue-200' : 'text-slate-900 bg-slate-200'} rounded-md`}>
                                    {(() => {
                                      const rawValue = gridData[organismKey]?.[location];
                                      if (!rawValue || rawValue === '0') return '0';
                                      const numValue = parseInt(String(rawValue).replace(/[^\d-]/g, ''));
                                      if (isNaN(numValue)) return rawValue || '0';
                                      return numValue.toLocaleString('nb-NO');
                                    })()}
                                  </div>
                                ) : (
                                  <div className="relative group/cell">
                                    <Input 
                                      value={formatDisplayValue(gridData[organismKey]?.[location] || '')}
                                      onChange={(e) => updateGridValue(organismKey, location, e.target.value)}
                                      onBlur={(e) => handleInputBlur(e, organismKey, location)}
                                      onContextMenu={(e) => handleRightClick(e, organismKey, location)}
                                      className={`h-8 text-sm text-center border-0 focus:ring-2 focus:ring-teal-500 rounded-md font-semibold cursor-pointer pr-8 ${getColorForValue(gridData[organismKey]?.[location] || '', organism.group, organism.species, organismKey, location)}`}
                                      title="Høyreklikk for å overstyre farge"
                                    />
                                    {/* Biovolume icon - show on hover only */}
                                    {gridData[organismKey]?.[location] && gridData[organismKey]?.[location] !== '' && gridData[organismKey]?.[location] !== '0' && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          const cellKey = `${organismKey}_${location}`;
                                          
                                          // Default classes (same as in AdminOrganismsNew.jsx)
                                          const defaultClasses = [
                                            { id: 'liten', name: 'Liten', biovolume: 100 },
                                            { id: 'median', name: 'Median', biovolume: 500 },
                                            { id: 'stor', name: 'Stor', biovolume: 2000 }
                                          ];
                                          
                                          // Use organism's classes or default classes
                                          const classes = organism.biovolume_classes?.length > 0 
                                            ? organism.biovolume_classes 
                                            : defaultClasses;
                                          
                                          setBiovolumCellData({
                                            organismKey,
                                            locationKey: location,
                                            value: gridData[organismKey]?.[location] || '',
                                            organism: { ...organism, biovolume_classes: classes }
                                          });
                                          
                                          // Load existing distribution or default to 100% Median
                                          const existingDist = biovolumData[cellKey]?.distribution;
                                          if (existingDist && Object.keys(existingDist).length > 0) {
                                            setBiovolumDistribution(existingDist);
                                          } else {
                                            // Default: 100% on Median
                                            const defaultDist = {};
                                            classes.forEach(cls => {
                                              const key = cls.id || cls.name;
                                              defaultDist[key] = (cls.name.toLowerCase() === 'median' || cls.id === 'median') ? 100 : 0;
                                            });
                                            setBiovolumDistribution(defaultDist);
                                          }
                                          setShowBiovolumDialog(true);
                                        }}
                                        className={`absolute right-0.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded flex items-center justify-center transition-all z-10 opacity-0 group-hover/cell:opacity-100 ${
                                          biovolumData[`${organismKey}_${location}`] 
                                            ? 'bg-purple-600 text-white shadow-md !opacity-100' 
                                            : 'bg-purple-100 text-purple-600 hover:bg-purple-500 hover:text-white border border-purple-300'
                                        }`}
                                        title="Fordel biovolum"
                                      >
                                        <BarChart3 className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            );
                          } else {
                            // Dynamic column cell
                            const column = col.data;
                            return (
                              <td key={column.id} className="border-r border-b border-slate-300 p-1 bg-yellow-50">
                                {column.location && !isSum && (
                                  <Input 
                                    value={formatDisplayValue(gridData[organismKey]?.[column.location] || '')}
                                    onChange={(e) => updateGridValue(organismKey, column.location, e.target.value)}
                                    className="h-8 text-sm text-center border-0 focus:ring-2 focus:ring-teal-500 rounded-md font-semibold bg-white"
                                  />
                                )}
                                {column.location && isSum && (
                                  <div className="h-8 flex items-center justify-center text-sm font-bold text-slate-900 bg-slate-200 rounded-md">
                                    {(() => {
                                      const rawValue = gridData[organismKey]?.[column.location];
                                      if (!rawValue || rawValue === '0') return '0';
                                      const numValue = parseInt(String(rawValue).replace(/[^\d-]/g, ''));
                                      if (isNaN(numValue)) return rawValue || '0';
                                      return numValue.toLocaleString('nb-NO');
                                    })()}
                                  </div>
                                )}
                                {!column.location && (
                                  <div className="h-8 flex items-center justify-center bg-slate-100 rounded-md border border-slate-200 border-dashed">
                                    {/* Empty cell - waiting for location selection */}
                                  </div>
                                )}
                              </td>
                            );
                          }
                        })}
                        </tr>
                    );
                  })}
                  </tbody>
                </table>
              </div>
              {/* Enhanced add buttons - Refactored component */}
              <AlgaeTableActions
                onAddOrganism={() => setShowManualForm(true)}
                onEditSchema={() => navigate('/admin/schema-builder')}
                onCancel={() => navigate('/admin/bulk-history')}
                onDelete={bulkId ? async () => {
                  const ok = await confirmDialog({
                    title: 'Slette skjema?',
                    message: 'Er du sikker på at du vil slette dette skjemaet? Handlinga kan ikkje angrast.',
                    confirmText: 'Ja, slett',
                    cancelText: 'Avbryt',
                    variant: 'danger'
                  });
                  if (ok) {
                    try {
                      await axios.delete(`${API}/bulk-registrations/${bulkId}`, { withCredentials: true });
                      toast.success('Skjema sletta');
                      navigate('/admin/bulk-history');
                    } catch (e) { toast.error('Kunne ikkje slette'); }
                  }
                } : null}
                onSubmit={handleSubmitAll}
                completionPercentage={getCompletionPercentage()}
                loading={loading}
                generatingSummary={generatingSummary}
                selectedArea={selectedArea}
                navigate={navigate}
                isEditing={!!bulkId}
              />
            </div>
            
            {/* Custom modal for adding new organism with proper z-index handling */}
            <ManualOrganismForm
              isOpen={showManualForm}
              onClose={() => {
                setShowManualForm(false);
                setManualGroup('');
                setManualSpecies('');
                setManualDepth('');
              }}
              manualGroup={manualGroup}
              onManualGroupChange={setManualGroup}
              manualSpecies={manualSpecies}
              onManualSpeciesChange={setManualSpecies}
              manualDepth={manualDepth}
              onManualDepthChange={setManualDepth}
              groupSearchTerm={groupSearchTerm}
              onGroupSearchChange={setGroupSearchTerm}
              speciesSearchTerm={speciesSearchTerm}
              onSpeciesSearchChange={setSpeciesSearchTerm}
              filteredGroups={getFilteredGroups()}
              filteredSpecies={getFilteredSpecies()}
              onAddClick={addManualRow}
              onShowAddGroupDialog={(searchTerm) => {
                setNewGroupName(searchTerm);
                setTimeout(() => setShowAddGroupDialog(true), 100);
              }}
              onShowAddSpeciesDialog={(searchTerm) => {
                setNewSpeciesName(searchTerm);
                setTimeout(() => setShowAddSpeciesDialog(true), 100);
              }}
            />
            
            {/* Dialog for adding new group */}
            <AddGroupDialog
              isOpen={showAddGroupDialog}
              onClose={setShowAddGroupDialog}
              groupName={newGroupName}
              onGroupNameChange={setNewGroupName}
              onAddClick={addNewGroup}
            />
            
            {/* Dialog for adding new species */}
            <AddSpeciesDialog
              isOpen={showAddSpeciesDialog}
              onClose={setShowAddSpeciesDialog}
              selectedGroup={manualGroup}
              speciesName={newSpeciesName}
              onSpeciesNameChange={setNewSpeciesName}
              onAddClick={addNewSpecies}
            />
          </div>
          )}
            
            {/* Zooplankton Table with fullscreen button — hidden when template has no zoo */}
            {hasZooplankton && (<>
            <div className="mt-12">
              <TableSectionHeader
                title="Zooplankton"
                onPasteClick={() => handlePasteClick('zooplankton')}
                onFullscreenClick={() => setFullscreenZooplankton(true)}
              />
              
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="relative overflow-x-auto overflow-y-auto max-h-[500px]">
                  <table className="border-collapse" style={{ minWidth: `${280 + currentLocations.length * 90}px` }}>
                    <thead className="sticky top-0 z-50">
                      <tr>
                        <th className="sticky left-0 z-[60] border-r border-b border-slate-300 px-3 py-2 text-left text-sm font-semibold bg-slate-50" style={{width: '130px', minWidth: '130px'}}>
                          Prøvetype
                        </th>
                        <th className="sticky z-[60] border-r border-b border-slate-300 px-3 py-2 text-left text-sm font-semibold bg-slate-50" style={{left: '130px', width: '150px', minWidth: '150px', boxShadow: '3px 0 6px rgba(0,0,0,0.08)'}}>
                          Organisme
                        </th>
                        {currentLocations.map((loc, index) => {
                          // Parse location - handles "A - Toftøy" format
                          let letter = String.fromCharCode(65 + index);
                          let name = 'Ny lokalitet';
                          if (loc) {
                            const parts = loc.split(' - ');
                            if (parts.length >= 2) {
                              const firstPart = parts[0].trim();
                              letter = firstPart.startsWith('Lokalitet ') ? firstPart.replace('Lokalitet ', '').trim() : firstPart;
                              name = parts.slice(1).join(' - ').trim();
                            } else {
                              name = loc;
                            }
                          }
                          return (
                            <th key={loc} className="border-r border-b border-slate-300 px-2 py-2 text-center text-sm font-semibold bg-slate-50 group relative" style={{minWidth: '85px'}}>
                              {/* Delete column - subtle */}
                              <button
                                onClick={() => removeStandardColumn(loc)}
                                className="absolute top-1 right-1 w-4 h-4 rounded-full opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 z-30"
                                title="Fjern kolonne"
                              >
                                <X className="w-3 h-3" />
                              </button>
                              
                              <div className="text-xs font-semibold text-slate-700">{letter} - {name}</div>
                              <div className="text-[10px] text-slate-400">(ant/m³)</div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {currentZooplanktonOrganisms.map((organism, index) => {
                        const organismKey = `${organism.sampleType}_${organism.organism}`;
                        const isCustom = organism.isCustom;
                        
                        return (
                          <tr 
                            key={organismKey}
                            className={`group relative hover:bg-slate-50 ${isCustom ? 'bg-yellow-50' : ''}`}
                            onContextMenu={(e) => handleZooplanktonRowRightClick(e, organismKey, organism)}
                          >
                            <td className={`sticky left-0 border-r border-b border-slate-300 px-3 py-2 text-sm ${isCustom ? 'bg-yellow-50' : 'bg-white'} relative`} style={{width: '130px', minWidth: '130px', zIndex: 40}}>
                              <div className="flex items-center justify-between">
                                <span>{organism.sampleType}</span>
                                {/* Plus button as inline element */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    insertZooplanktonRowAfter(index);
                                  }}
                                  className="ml-2 w-6 h-6 bg-teal-600 hover:bg-teal-700 text-white rounded-full opacity-0 group-hover:opacity-100 hover:!opacity-100 transition-all duration-200 flex items-center justify-center shadow-lg hover:scale-110 cursor-pointer flex-shrink-0"
                                  title="Legg til rad etter"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                            <td className={`sticky border-r border-b border-slate-300 px-3 py-2 text-sm ${isCustom ? 'bg-yellow-50' : 'bg-white'}`} style={{zIndex: 40, left: '130px', width: '150px', minWidth: '150px', boxShadow: '3px 0 6px rgba(0,0,0,0.08)'}}>
                              <div className="flex items-center justify-between">
                                <span className="italic">{organism.organism}</span>
                                {isCustom && (
                                  <div className="flex items-center space-x-1">
                                    <span className="text-xs bg-yellow-200 text-yellow-800 px-1 rounded">ny</span>
                                    <Button
                                      onClick={() => removeZooplanktonRow(organismKey)}
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 w-5 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      title="Slett rad"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </td>
                            {currentLocations.map(location => {
                              const cellKey = `${organismKey}_${location}`;
                              const cellValue = zooplanktonGridData[organismKey]?.[location] || '';
                              const hasColorOverride = colorOverrides[cellKey];
                              
                              // White by default, green when has content, or override color
                              const colorClass = hasColorOverride 
                                ? hasColorOverride === 'green' ? 'bg-emerald-100' 
                                  : hasColorOverride === 'yellow' ? 'bg-amber-100'
                                  : hasColorOverride === 'red' ? 'bg-red-100'
                                  : 'bg-white'
                                : cellValue ? 'bg-emerald-100' : 'bg-white';
                              
                              return (
                                <td 
                                  key={location} 
                                  className="border-r border-b border-slate-300 p-1"
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation(); // Prevent event from bubbling to row handler
                                    
                                    // Calculate menu position with viewport boundary checks
                                    const menuWidth = 200;
                                    const menuHeight = 300;
                                    
                                    let x = e.clientX;
                                    let y = e.clientY;
                                    
                                    if (x + menuWidth > window.innerWidth) {
                                      x = window.innerWidth - menuWidth - 10;
                                    }
                                    
                                    if (y + menuHeight > window.innerHeight) {
                                      y = window.innerHeight - menuHeight - 10;
                                    }
                                    
                                    x = Math.max(10, x);
                                    y = Math.max(10, y);
                                    
                                    setContextMenu({
                                      x,
                                      y,
                                      organismKey,
                                      location
                                    });
                                  }}
                                >
                                  <Input 
                                    value={formatZooplanktonDisplayValue(cellValue)}
                                    onChange={(e) => updateZooplanktonValue(organismKey, location, e.target.value)}
                                    onBlur={(e) => {
                                      const rawValue = getRawValue(e.target.value);
                                      if (!rawValue || rawValue.trim() === '') {
                                        // Fill with organism's default zero value on tab-out
                                        const zeroVal = organism.default_value || '<5';
                                        updateZooplanktonValue(organismKey, location, zeroVal);
                                      }
                                      // Schedule save after React state flush (ref-based for latest data)
                                      setTimeout(() => saveNow(), 150);
                                    }}
                                    className={`h-8 text-sm text-center border-0 focus:ring-1 focus:ring-teal-500 ${colorClass}`}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            {/* Modal for adding new zooplankton organism */}
            <AddZooplanktonForm
              isOpen={showZooplanktonForm}
              onClose={() => setShowZooplanktonForm(false)}
              zooplanktonSampleType={zooplanktonSampleType}
              setZooplanktonSampleType={setZooplanktonSampleType}
              zooplanktonOrganism={zooplanktonOrganism}
              setZooplanktonOrganism={setZooplanktonOrganism}
              sampleTypeSearchTerm={sampleTypeSearchTerm}
              setSampleTypeSearchTerm={setSampleTypeSearchTerm}
              zooplanktonOrganismSearchTerm={zooplanktonOrganismSearchTerm}
              setZooplanktonOrganismSearchTerm={setZooplanktonOrganismSearchTerm}
              getFilteredSampleTypes={getFilteredSampleTypes}
              getFilteredZooplanktonOrganisms={getFilteredZooplanktonOrganisms}
              setNewSampleTypeName={setNewSampleTypeName}
              setShowAddSampleTypeDialog={setShowAddSampleTypeDialog}
              setNewZooplanktonOrganismName={setNewZooplanktonOrganismName}
              setShowAddZooplanktonOrganismDialog={setShowAddZooplanktonOrganismDialog}
              onAddClick={addZooplanktonRow}
            />
            
            {/* Dialog for adding new sample type */}
            <AddSampleTypeDialog
              isOpen={showAddSampleTypeDialog}
              onClose={() => setShowAddSampleTypeDialog(false)}
              sampleTypeName={newSampleTypeName}
              onSampleTypeNameChange={setNewSampleTypeName}
              onAddClick={addNewSampleType}
            />
            
            {/* Dialog for adding new zooplankton organism */}
            <AddZooplanktonOrganismDialog
              isOpen={showAddZooplanktonOrganismDialog}
              onClose={() => setShowAddZooplanktonOrganismDialog(false)}
              sampleType={zooplanktonSampleType}
              organismName={newZooplanktonOrganismName}
              onOrganismNameChange={setNewZooplanktonOrganismName}
              onAddClick={addNewZooplanktonOrganism}
            />
            </>)}
        </>
        )}
        </div>
        </div>
        
        {/* AI Summary Step Modal */}
        <SummaryModal
          isOpen={showSummaryStep}
          onClose={() => setShowSummaryStep(false)}
          summary={editableSummary}
          onSummaryChange={setEditableSummary}
          onRegenerateClick={generateAISummary}
          onSaveDraft={saveWithoutSending}
          onSendToCustomer={finalSave}
          loading={loading}
          generatingSummary={generatingSummary}
        />
        
        {/* Fullscreen Algae Modal */}
        <FullscreenAlgaeModal
          isOpen={fullscreenAlgae}
          onClose={() => setFullscreenAlgae(false)}
          organisms={organisms}
          customOrganisms={customOrganisms}
          locations={currentLocations.length > 0 ? currentLocations : locations}
          gridData={gridData}
          depthOverrides={depthOverrides}
          updateGridValue={updateGridValue}
          handleInputBlur={handleInputBlur}
          getColorForValue={getColorForValue}
          handleRightClick={handleRightClick}
        />
        
        {/* Fullscreen Zooplankton Modal */}
        <FullscreenZooplanktonModal
          isOpen={fullscreenZooplankton}
          onClose={() => setFullscreenZooplankton(false)}
          zooplanktonOrganisms={zooplanktonOrganisms}
          locations={currentLocations.length > 0 ? currentLocations : locations}
          zooplanktonGridData={zooplanktonGridData}
          colorOverrides={colorOverrides}
          updateZooplanktonValue={updateZooplanktonValue}
          formatZooplanktonDisplayValue={formatZooplanktonDisplayValue}
          setContextMenu={setContextMenu}
        />
        
        {/* Right-click context menu for color override */}
        <ContextMenu
          contextMenu={contextMenu}
          onClose={() => setContextMenu(null)}
          onRemoveRow={removeRow}
          onRemoveZooplanktonRow={removeZooplanktonRow}
          onRemoveColumn={removeStandardColumn}
          onColorOverride={(color) => {
            if (color === null) {
              removeColorOverride();
            } else {
              setColorOverride(color);
            }
          }}
        />
      </div>
      
      {/* New Location Modal - Enhanced with Map and Address Search */}
      <LocationModal
        isOpen={showNewLocationModal}
        onClose={(open) => {
          setShowNewLocationModal(open);
          if (!open) {
            resetLocationForm();
            setPendingColumnId(null);
          }
        }}
        areas={areas}
        areaId={newLocationAreaId}
        onAreaIdChange={setNewLocationAreaId}
        letter={newLocationLetter}
        onLetterChange={setNewLocationLetter}
        name={newLocationName}
        onNameChange={setNewLocationName}
        address={newLocationAddress}
        onAddressChange={setNewLocationAddress}
        latitude={newLocationLatitude}
        onLatitudeChange={setNewLocationLatitude}
        longitude={newLocationLongitude}
        onLongitudeChange={setNewLocationLongitude}
        onCreateClick={createNewLocation}
        loading={loading}
      />
      
      {/* Paste Preview Dialog */}
      <PastePreviewDialog
        isOpen={showPasteDialog}
        onClose={setShowPasteDialog}
        previewData={pastePreviewData}
        targetTable={pasteTargetTable}
        onImportClick={applyPastedData}
      />
      
      {/* Save Template Dialog */}
      <SaveTemplateDialog
        open={showSaveTemplateDialog}
        onClose={() => setShowSaveTemplateDialog(false)}
        onSave={handleSaveTemplate}
        isUpdating={isUpdatingTemplate}
        currentTemplate={availableTemplates.find(t => t.id === selectedTemplate)}
      />
      
      {/* Sum Threshold Dialog */}
      {showSumThresholdDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-[450px] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-slate-900">
                Grenseverdier for {sumThresholdGroup} Sum
              </h3>
              <button 
                onClick={() => setShowSumThresholdDialog(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <label className="block text-sm font-medium text-green-800 mb-2">
                  🟢 Grønn grense (under)
                </label>
                <input
                  type="number"
                  value={sumThresholds[sumThresholdGroup]?.yellow || ''}
                  onChange={(e) => setSumThresholds(prev => ({
                    ...prev,
                    [sumThresholdGroup]: { ...prev[sumThresholdGroup], yellow: parseInt(e.target.value) || 0 }
                  }))}
                  placeholder="f.eks. 100000"
                  className="w-full px-4 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              
              <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                <label className="block text-sm font-medium text-yellow-800 mb-2">
                  🟡 Gul grense (under)
                </label>
                <input
                  type="number"
                  value={sumThresholds[sumThresholdGroup]?.orange || ''}
                  onChange={(e) => setSumThresholds(prev => ({
                    ...prev,
                    [sumThresholdGroup]: { ...prev[sumThresholdGroup], orange: parseInt(e.target.value) || 0 }
                  }))}
                  placeholder="f.eks. 500000"
                  className="w-full px-4 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                />
              </div>
              
              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <label className="block text-sm font-medium text-orange-800 mb-2">
                  🟠 Oransje grense (under)
                </label>
                <input
                  type="number"
                  value={sumThresholds[sumThresholdGroup]?.red || ''}
                  onChange={(e) => setSumThresholds(prev => ({
                    ...prev,
                    [sumThresholdGroup]: { ...prev[sumThresholdGroup], red: parseInt(e.target.value) || 0 }
                  }))}
                  placeholder="f.eks. 1000000"
                  className="w-full px-4 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              
              <p className="text-xs text-slate-500 text-center">
                Verdier over oransje grense vises som 🔴 rød
              </p>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowSumThresholdDialog(false)}
              >
                Avbryt
              </Button>
              <Button
                onClick={async () => {
                  try {
                    // Save sum thresholds to backend
                    await axios.post(`${API}/thresholds/sum`, {
                      group: sumThresholdGroup,
                      thresholds: sumThresholds[sumThresholdGroup]
                    }, { withCredentials: true });
                    toast.success(`Grenseverdier for ${sumThresholdGroup} Sum lagret!`);
                    setShowSumThresholdDialog(false);
                  } catch (error) {
                    console.error('Error saving sum thresholds:', error);
                    toast.error('Kunne ikke lagre grenseverdier');
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Lagre
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Biovolume Distribution Dialog */}
      <BiovolumDialog
        isOpen={showBiovolumDialog}
        onClose={() => setShowBiovolumDialog(false)}
        cellData={biovolumCellData}
        distribution={biovolumDistribution}
        setDistribution={setBiovolumDistribution}
        onSave={(data) => {
          const cellKey = `${biovolumCellData.organismKey}_${biovolumCellData.locationKey}`;
          setBiovolumData(prev => ({
            ...prev,
            [cellKey]: data
          }));
        }}
      />

      {/* Temperature input modal */}
      {tempModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setTempModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xs mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="temp-modal">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900 text-sm">Temperatur</h3>
              <p className="text-xs text-slate-500">{tempModalLabel}</p>
            </div>
            <div className="px-5 py-4">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.1"
                  value={tempModalValue}
                  onChange={e => setTempModalValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveTempModal(); }}
                  className="flex-1 h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="t.d. 7.2"
                  autoFocus
                  data-testid="temp-input"
                />
                <span className="text-sm text-slate-500 font-medium">°C</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50">
              {temperatureData[tempModalLoc] && (
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 mr-auto" onClick={() => {
                  setTemperatureData(prev => { const u = { ...prev }; delete u[tempModalLoc]; return u; });
                  setTempModalOpen(false);
                }}>Slett</Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setTempModalOpen(false)}>Avbryt</Button>
              <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={saveTempModal} data-testid="temp-save-btn">Lagre</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}